import React, { useEffect, useRef, useState } from "react";
import { Link as LinkIcon, Paperclip } from "lucide-react";

// Display label used in the textarea AND in update bodies before the
// resolver swaps it back to the id form. Falls back to email then a generic
// "user" so an unresolvable employee never produces an empty chip.
export function displayName(e) {
  if (!e) return "user";
  const n = `${e.firstname || ""} ${e.lastname || ""}`.trim();
  return n || e.email || "user";
}

// Convert "@<Display Name>" occurrences in `body` to "@<userId>" so the
// backend MENTION_RE (/@[a-f0-9]{24}/) finds them. Longer names match first
// so "@John Smith" wins over "@John". Names that don't resolve are left as
// plain text (they won't trigger a notification — correct behavior).
export function resolveMentions(body, employees) {
  if (!body || !employees?.length) return body;
  const sorted = [...employees].sort(
    (a, b) => displayName(b).length - displayName(a).length,
  );
  let out = body;
  for (const e of sorted) {
    const name = displayName(e);
    if (!name) continue;
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`@${escaped}(?=$|[\\s.,;:!?])`, "g");
    out = out.replace(re, `@${e._id}`);
  }
  return out;
}

// Textarea wrapper with @ autocomplete. When the user types "@" followed by
// non-space chars, opens a popup listing employees whose first/last name or
// email start with that query. Selecting one inserts "@<DisplayName>" at the
// cursor — `resolveMentions` swaps that for the id form just before submit.
//
// Tiny + dependency-free; no portal, so the popup is positioned absolutely
// inside this component's relative wrapper.
export default function MentionInput({
  value,
  onChange,
  placeholder,
  rows = 3,
  employees = [],
  onSubmit,
  // Optional: enable paste/drop image uploads.
  onUpload,
}) {
  const ref = useRef(null);
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  // Position of the @ trigger character — used to know where to insert.
  const [triggerAt, setTriggerAt] = useState(-1);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const matches = open
    ? employees
        .filter((e) => {
          if (!query) return true;
          const name = `${e.firstname || ""} ${e.lastname || ""}`.toLowerCase();
          const email = (e.email || "").toLowerCase();
          const q = query.toLowerCase();
          return name.includes(q) || email.includes(q);
        })
        .slice(0, 8)
    : [];

  useEffect(() => {
    if (matches.length === 0 && open) setActiveIdx(0);
    if (activeIdx >= matches.length) setActiveIdx(Math.max(0, matches.length - 1));
  }, [matches.length, open, activeIdx]);

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);

    // Look backwards from caret to find an @ trigger (no whitespace between).
    const caret = e.target.selectionStart;
    let i = caret - 1;
    let buf = "";
    while (i >= 0) {
      const ch = v[i];
      if (ch === "@") {
        setTriggerAt(i);
        setQuery(buf);
        setOpen(true);
        setActiveIdx(0);
        return;
      }
      if (/\s/.test(ch)) break;
      buf = ch + buf;
      i -= 1;
    }
    setOpen(false);
    setTriggerAt(-1);
  };

  const insertMention = (employee) => {
    if (triggerAt < 0 || !ref.current) return;
    const before = value.slice(0, triggerAt);
    const caret = ref.current.selectionStart;
    const after = value.slice(caret);
    const name = displayName(employee);
    const next = `${before}@${name} ${after}`;
    onChange(next);
    setOpen(false);
    setTriggerAt(-1);

    requestAnimationFrame(() => {
      if (!ref.current) return;
      const pos = before.length + 1 + name.length + 1;
      ref.current.selectionStart = ref.current.selectionEnd = pos;
      ref.current.focus();
    });
  };

  const handleKeyDown = (e) => {
    if (open && matches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(matches[activeIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
    }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) {
      e.preventDefault();
      onSubmit();
      return;
    }
    // Familiar text-editor shortcuts. Cmd/Ctrl + B / I / K (link).
    if (e.metaKey || e.ctrlKey) {
      const k = e.key.toLowerCase();
      if (k === "b") { e.preventDefault(); wrapInline("**", "**", "bold"); return; }
      if (k === "i") { e.preventDefault(); wrapInline("*", "*", "italic"); return; }
      if (k === "k") { e.preventDefault(); insertLink(); return; }
    }
  };

  // Inserts a markdown image reference at the current caret. Used by both
  // paste and drop. Pulls the most recent value from the textarea so we
  // never insert into a stale prop.
  const insertAtCaret = (snippet) => {
    const el = ref.current;
    const curVal = el ? el.value : value;
    const caret = el ? el.selectionStart : curVal.length;
    const next = `${curVal.slice(0, caret)}${snippet}${curVal.slice(caret)}`;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = caret + snippet.length;
      el.selectionStart = el.selectionEnd = pos;
      el.focus();
    });
  };

  // Uploads one or more files. Inserts a placeholder for each, swaps it
  // for the real `![filename](attId)` (or `[filename](attId)` for non-image
  // attachments) once uploaded. Errors leave the placeholder in place.
  const uploadFiles = async (files) => {
    if (!onUpload || !files?.length) return;
    setUploading(true);
    for (const file of files) {
      const placeholder = `![uploading ${file.name}...]() `;
      insertAtCaret(placeholder);
      try {
        const att = await onUpload(file);
        const ref2 = att.mime?.startsWith("image/")
          ? `![${att.filename}](${att._id})`
          : `[${att.filename}](${att._id})`;
        // Replace the first occurrence of the placeholder in the latest body.
        const cur = ref.current?.value ?? value;
        const updated = cur.replace(placeholder, `${ref2} `);
        onChange(updated);
      } catch (err) {
        const cur = ref.current?.value ?? value;
        const updated = cur.replace(placeholder, `[upload failed: ${err?.message || file.name}] `);
        onChange(updated);
      }
    }
    setUploading(false);
  };

  const handlePaste = (e) => {
    if (!onUpload) return;
    const items = Array.from(e.clipboardData?.items || []);
    const files = items
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter(Boolean);
    if (!files.length) return;
    e.preventDefault();
    uploadFiles(files);
  };
  const handleDrop = (e) => {
    if (!onUpload) return;
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) uploadFiles(files);
  };
  const handleDragOver = (e) => {
    if (!onUpload) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };
  const handleDragLeave = () => setDragOver(false);

  // ── Formatting toolbar helpers ──────────────────────────────────
  // All toolbar buttons operate on the textarea selection / current line
  // via Markdown-ish wrappers. Storage stays plain text; MentionText
  // renders the result. Pattern: `wrapInline("**","**")` wraps selection
  // with bold markers (or inserts them at the caret if no selection).
  const wrapInline = (prefix, suffix = prefix, placeholder = "") => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const cur = el.value;
    const sel = cur.slice(start, end);
    const body = sel || placeholder;
    const next = cur.slice(0, start) + prefix + body + suffix + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      const inner = start + prefix.length;
      ref.current.focus();
      ref.current.selectionStart = inner;
      ref.current.selectionEnd = inner + body.length;
    });
  };

  // Prepends `linePrefix` to every line covered by the current selection.
  // Empty selection: prefixes the current line. Used for bullet/numbered
  // lists where one button toggles a "- " or "1. " at the line start.
  const prefixLines = (linePrefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const cur = el.value;
    // Expand selection to whole lines.
    const lineStart = cur.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = cur.indexOf("\n", end);
    const sliceEnd = lineEnd === -1 ? cur.length : lineEnd;
    const chunk = cur.slice(lineStart, sliceEnd);
    const lines = chunk.split("\n");
    // Numbered list: replace prefix with sequential N. — otherwise apply
    // the literal prefix to each line. Empty lines pass through untouched
    // so we don't leave stray bullets.
    const numbered = linePrefix === "1. ";
    const rebuilt = lines
      .map((ln, i) => (ln.length === 0 ? ln : (numbered ? `${i + 1}. ` : linePrefix) + ln))
      .join("\n");
    const next = cur.slice(0, lineStart) + rebuilt + cur.slice(sliceEnd);
    onChange(next);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      ref.current.selectionStart = lineStart;
      ref.current.selectionEnd = lineStart + rebuilt.length;
    });
  };

  const insertLink = () => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const cur = el.value;
    const sel = cur.slice(start, end);
    const url = window.prompt("URL", "https://");
    if (!url) return;
    const text = sel || "link";
    const snippet = `[${text}](${url})`;
    const next = cur.slice(0, start) + snippet + cur.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      // Select the link text so the user can immediately retype if needed.
      const innerStart = start + 1;
      const innerEnd = innerStart + text.length;
      ref.current.selectionStart = innerStart;
      ref.current.selectionEnd = innerEnd;
    });
  };

  const onFilesChosen = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    // Reset so picking the same file twice in a row fires onChange.
    e.target.value = "";
  };

  return (
    <div className={`mention-input-wrap ${dragOver ? "drag-over" : ""}`}>
      <div className="update-toolbar" onMouseDown={(e) => e.preventDefault()}>
        <button type="button" className="tb-btn" title="Bold (Ctrl+B)" onClick={() => wrapInline("**", "**", "bold")}>
          <strong>B</strong>
        </button>
        <button type="button" className="tb-btn" title="Italic (Ctrl+I)" onClick={() => wrapInline("*", "*", "italic")}>
          <em>I</em>
        </button>
        <button type="button" className="tb-btn" title="Inline code" onClick={() => wrapInline("`", "`", "code")}>
          {"<>"}
        </button>
        <span className="tb-sep" />
        <button type="button" className="tb-btn" title="Bulleted list" onClick={() => prefixLines("- ")}>
          • List
        </button>
        <button type="button" className="tb-btn" title="Numbered list" onClick={() => prefixLines("1. ")}>
          1. List
        </button>
        <span className="tb-sep" />
        <button type="button" className="tb-btn" title="Insert link" onClick={insertLink}>
          <LinkIcon size={12} className="me-1" /> Link
        </button>
        {onUpload && (
          <>
            <span className="tb-sep" />
            <button
              type="button"
              className="tb-btn tb-upload"
              title="Attach a file or image"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              <Paperclip size={12} className="me-1" /> Upload
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={onFilesChosen}
            />
          </>
        )}
        <span className="tb-hint">**bold** · *italic* · `code` · [text](url)</span>
      </div>
      <textarea
        ref={ref}
        className="update-input"
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      />
      {uploading && (
        <div className="upload-banner">Uploading… (you can keep typing)</div>
      )}
      {onUpload && dragOver && (
        <div className="drop-overlay">Drop image to insert</div>
      )}
      {open && matches.length > 0 && (
        <div className="mention-popup">
          {matches.map((e, i) => {
            const name = `${e.firstname || ""} ${e.lastname || ""}`.trim() || e.email;
            const initials =
              ((e.firstname?.[0] || "") + (e.lastname?.[0] || "")).toUpperCase() || "?";
            return (
              <button
                type="button"
                key={e._id}
                className={`mention-popup-item ${i === activeIdx ? "active" : ""}`}
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => insertMention(e)}
              >
                <span className="avatar-chip">{initials}</span>
                <span className="mention-popup-name">{name}</span>
                <span className="mention-popup-email">{e.email}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
